import {
  ResponsiveContainer,
  Cell,
  Tooltip,
  Legend,
  BarChart,
  Bar,
  YAxis,
  CartesianGrid,
  XAxis,
} from 'recharts';
import './chart.css';
import { Box, Flex } from '@chakra-ui/react';
import CustomTooltip from './components/customTooltip/CustomTooltip';
import {
  ISSUE_AGE_CHART_KEYS,
  ISSUE_CHART_KEYS,
  ISSUE_LIFECYCLE_DIST_CHARTS,
  PR_AGEING_CHARTS,
} from '../shared/utils';
import { useCallback, useState } from 'react';
import MetricSidebar from './components/MetricSidebar';
import moment from 'moment';
import { StyledToopTipText } from './components/customTooltip/CustomTooltip.styled';
import { DateRangeSelection } from '../shared/typings';

export interface CategoryBarChartDataItem {
  label: string;
  color: string;
  value: number;
  actualVal: string;
  name: string;
}

export interface CategoryBarChartKey {
  key: string;
  name: string;
  color: string;
  chartType: string | null;
}

interface PieChartRechartsProps {
  data: CategoryBarChartDataItem[];
  keys?: CategoryBarChartKey[];
  chartMetadata?: any;
  showLabel?: boolean;
  selectedTeam: string;
  hideBtn?: boolean;
  slant?: boolean;
  ticketType?: string;
  selected: DateRangeSelection;
  queryKey?: string;
  repos?: string[];
}

export const CategoryBarChart = ({
  data,
  keys,
  chartMetadata,
  showLabel,
  selectedTeam,
  hideBtn,
  slant,
  ticketType,
  selected,
  queryKey,
  repos,
}: PieChartRechartsProps) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [hoveredIndex, setHoveredIndex] = useState<any>(null);
  const [isTooltipHovered, setIsTooltipHovered] = useState(false);
  const [hideTooltip, setHideTooltip] = useState(false);

  const handleTooltipHoverChange = (hovered: boolean) => {
    setIsTooltipHovered(hovered);
  };

  const [clickedData, setClickedData] = useState<any>(null);
  const [currentChartMeta, setCurrentChartMeta] = useState<any>(null);

  const RenderCustomLegend = () => {
    return (
      <Flex justify="space-between" direction={'column'} pr={3}>
        <Flex align="center">
          <Box mr={1} height="10px" width="10px" bg={'#B10707'} />
          <StyledToopTipText>Blocked Category Status</StyledToopTipText>
        </Flex>
        <Flex align="center">
          <Box mr={1} height="10px" width="10px" bg={'#2B2689'} />
          <StyledToopTipText>Unblocked Category Status</StyledToopTipText>
        </Flex>
      </Flex>
    );
  };

  const handleModalClose = useCallback(() => {
    setIsOpen(false);
  }, [isOpen]);

  const handleModalOpen = useCallback(() => {
    setIsOpen(true);
  }, [isOpen]);
  return (
    <>
      <Box
        position="relative"
        width="100%"
        height={showLabel ? '100%' : '240px'}
      >
        <ResponsiveContainer>
          <BarChart data={data}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} />
            <XAxis
              dataKey="name"
              type="category"
              style={{ fontSize: 11, fontWeight: 500 }}
              label={{
                value: 'Status',
                fontSize: 11,
                position: 'insideBottom',
                fontWeight: 400,
                dy: 10,
              }}
            />
            <YAxis
              type="number"
              tickLine={false}
              tick={true}
              style={{
                fontSize: 11,
                fontWeight: 500,
              }}
              width={65}
              label={{
                value: '% Time spent in Status',
                angle: -90,
                position: 'insideLeft',
                fontSize: 11,
                fontWeight: 400,
                dy: 55,
              }}
            />
            <Tooltip
              wrapperStyle={{ outline: 'none' }}
              cursor={{ fill: 'transparent' }}
              content={
                <CustomTooltip
                  hideBtn
                  pointerEvents="none"
                  hideTooltip={hideTooltip}
                  onHoverChange={handleTooltipHoverChange}
                  trigger={isTooltipHovered ? 'click' : 'hover'}
                  chartKey={chartMetadata?.chartKey}
                />
              }
            />

            <Legend
              verticalAlign="middle"
              layout="vertical"
              iconType="circle"
              iconSize={11}
              align="right"
              wrapperStyle={{
                right: -8,
                fontSize: 11.5,
                top: 0,
              }}
              content={<RenderCustomLegend />}
            />

            <Bar
              key={'value'}
              dataKey={'value'}
              barSize={20}
              fill={'red'}
              style={{ cursor: 'pointer' }}
              onClick={(event: any, payload: any) => {
                handleModalOpen();
                setCurrentChartMeta(chartMetadata);
                setClickedData({
                  name: event.name,
                });
              }}
            >
              {data?.map((el) => (
                <Cell key={el.name} fill={el.color} name={el.name} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </Box>

      {[
        ...ISSUE_CHART_KEYS,
        ...ISSUE_AGE_CHART_KEYS,
        ...PR_AGEING_CHARTS,
        ...ISSUE_LIFECYCLE_DIST_CHARTS,
      ].includes(currentChartMeta?.chartKey) &&
        isOpen && (
          <MetricSidebar
            querykey={queryKey}
            openDrawer={isOpen}
            handleDrawerClose={handleModalClose}
            heading={chartMetadata?.chartTitle}
            subHeading={`${moment(selected?.startDate).format(
              'DD MMM'
            )} - ${moment(selected?.endDate).format('DD MMM')}, ${
              clickedData?.name
            } (${data.find((d) => d?.label === clickedData?.name)?.value} %)`}
            payload={{
              ...clickedData,

              payload: {
                startDate: selected?.startDate,
                endDate: selected?.endDate,
              },
            }}
            currentChartMeta={currentChartMeta}
            filterBy={{
              teamId: selectedTeam,
              type: ticketType,
              age:
                chartMetadata.chartKey === 'ISSUE_PROGRESSION_RATE'
                  ? null
                  : clickedData?.name,

              projectsFilter: repos,
              sprintId: selected.sprintId,
              status:
                chartMetadata.chartKey === 'ISSUE_PROGRESSION_RATE'
                  ? clickedData?.name
                  : '',
            }}
            selectedTeam={selectedTeam}
          />
        )}
    </>
  );
};
